// src/pages/LoanKycDetails.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchAllLoans } from "../redux/slices/loan/loanReportSlice";
import {
  uploadCustomerKyc,
  fetchUploadedKyc,
  approveKycDocument,
  rejectKycDocument,
} from "../redux/slices/Customer/CustomerKycSlice";
import "../assets/Loan/LoanKycDetails.css";
import { toast } from "react-toastify";

export default function LoanKycDetails() {
  const { loanId } = useParams();
  const dispatch = useDispatch();

  const [docType, setDocType] = useState("");
  const [file, setFile] = useState(null);

  const { loans = [], loading, error } = useSelector(
    (state) => state.loanReport
  );
  const {
    uploadedKyc = {},
    loading: kycLoading,
    error: kycError,
  } = useSelector((state) => state.customerKyc);

  const loan = loans.find((l) => String(l.id) === String(loanId));

  useEffect(() => {
    if (loan?.userId) {
      dispatch(fetchUploadedKyc(loan.userId));
    }
  }, [dispatch, loan?.userId]);

  const userDocs = loan?.userId ? uploadedKyc[loan.userId] || [] : [];

  const handleUpload = (e) => {
    e.preventDefault();

    if (!docType) {
      toast.warn("Please select a document type.");
      return;
    }
    if (!file) {
      toast.warn("Please choose a file to upload.");
      return;
    }

    dispatch(
      uploadCustomerKyc({
        userId: loan.userId,
        docName: docType,
        status: "Pending",
        file,
        createdBy: localStorage.getItem("username") || "System",
        createdId: localStorage.getItem("userid") || "0",
      })
    )
      .unwrap()
      .then(() => {
        toast.success(`✅ KYC document uploaded successfully for Loan #${loan.id}`);
        setDocType("");
        setFile(null);
        dispatch(fetchUploadedKyc(loan.userId));
      })
      .catch((err) => {
        toast.error(`❌ Failed to upload: ${err}`);
      });
  };

  const handleApprove = (doc) => {
    dispatch(
      approveKycDocument({
        userId: loan.userId,
        docId: doc.id,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(`✅ Document approved: ${doc.docName}`);
        dispatch(fetchUploadedKyc(loan.userId));
      })
      .catch((err) => {
        toast.error(`❌ Failed to approve: ${err}`);
      });
  };

  const handleReject = (doc) => {
    dispatch(
      rejectKycDocument({
        userId: loan.userId,
        docId: doc.id,
      })
    )
      .unwrap()
      .then(() => {
        toast.error(`❌ Document rejected: ${doc.docName}`);
        dispatch(fetchUploadedKyc(loan.userId));
      })
      .catch((err) => {
        toast.error(`❌ Failed to reject: ${err}`);
      });
  };

  return (
    <div className="LoanKycDetails-container">
      {loading ? (
        <p className="LoanKycDetails-loading">Loading...</p>
      ) : error ? (
        <p className="LoanKycDetails-error">{error}</p>
      ) : loan ? (
        <>
          {/* Loan Details */}
          <div className="LoanKycDetails-loanInfo">
            <h2>Loan Details - #{loan.id}</h2>
            <table className="LoanKycDetails-table">
              <tbody>
                <tr>
                  <td><strong>User ID</strong></td>
                  <td>{loan.userId || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Account Number</strong></td>
                  <td>{loan.loanAccountNumber || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Customer Name</strong></td>
                  <td>{loan.userName || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Loan Amount</strong></td>
                  <td>₹{Number(loan.fixedLoanAmount || 0).toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td><strong>ROI (%)</strong></td>
                  <td>{loan.loanRoi ?? "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Total Interest Payable</strong></td>
                  <td>₹{loan.loanInterest ?? "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Recovery Frequency</strong></td>
                  <td>{loan.loanRecoveryFrequency || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Applied Date</strong></td>
                  <td>{loan.loanAppliedDate?.slice(0, 10) || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Approval Date</strong></td>
                  <td>{loan.approvalDate?.slice(0, 10) || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Disbursal Date</strong></td>
                  <td>{loan.disbursalDate?.slice(0, 10) || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td>{loan.status || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>EMI Amount</strong></td>
                  <td>₹{Number(loan.loanEmiAmount || 0).toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td><strong>Total Paid</strong></td>
                  <td>₹{Number(loan.loanPayable || 0).toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td><strong>Guarantor Name</strong></td>
                  <td>{loan.guarantorName || "N/A"}</td>
                </tr>
                <tr>
                  <td><strong>Loan Type</strong></td>
                  <td>{loan.loanType || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* KYC Upload */}
          <div className="LoanKycDetails-kycSection">
            <h3>KYC Document Upload</h3>
            <form onSubmit={handleUpload} className="LoanKycDetails-kycForm">
              <label>
                Document Type:
                <select value={docType} onChange={(e) => setDocType(e.target.value)} required>
                  <option value="">-- Select Document --</option>
                  <option value="Aadhaar">Aadhaar</option>
                  <option value="PAN">PAN</option>
                  <option value="Voter ID">Voter ID</option>
                  <option value="Passport">Passport</option>
                  <option value="Driving License">Driving License</option>
                </select>
              </label>

              <label>
                Upload File:
                <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files[0])} required />
              </label>

              <button type="submit" className="LoanKycDetails-uploadBtn" disabled={kycLoading}>
                {kycLoading ? "Uploading..." : "Upload"}
              </button>
            </form>
          </div>

          {/* Uploaded KYC Table */}
          <div className="LoanKycDetails-docsTableWrapper">
            <h3 className="LoanKycDetails-docsTableHeader">KYC Uploaded Documents for Loan</h3>

            <table className="LoanKycDetails-docsTable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Document Type</th>
                  <th>Status</th>
                  <th>Uploaded On</th>
                  <th>Preview</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {userDocs.map((doc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{doc.docName}</td>
                    <td>
                      <span
                        className={`loankyc-status-badge loankyc-status-${doc.status?.toLowerCase()}`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td>{doc.createdAt ? doc.createdAt.slice(0, 10) : "N/A"}</td>
                    <td>
                      {doc.fileUrl ? (
                        <a href={doc.fileUrl} target="_blank" rel="noreferrer">View</a>
                      ) : "N/A"}
                    </td>
                    <td>
                      {doc.status?.toLowerCase() === "pending" && (
                        <>
                          <button
                            className="loankyc-approve-btn"
                            onClick={() => handleApprove(doc)}
                          >
                            Approve
                          </button>
                          <button
                            className="loankyc-reject-btn"
                            onClick={() => handleReject(doc)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="LoanKycDetails-noLoan">No loan details found.</p>
      )}
    </div>
  );
}
